<script lang="ts">
  // packages
  import { intcomma, pluralize } from 'journalize';

  // components
  import CandidateRow from './CandidateRow.svelte';
  import CandidateRowHeader from './CandidateRowHeader.svelte';

  // utils
  import { partyClass } from '../utils/race-helpers.js';

  // types
  import type { Choice, CrosswalkImage } from '../types';

  export let raceId: string;
  /** Already in display order (orderedChoices) */
  export let candidates: Choice[];
  export let totalVotes: number;
  export let isBallotMeasure: boolean;
  export let eevp: number | null;
  /** Not displayed yet; for multi-seat races (e.g. SF, 3 winners) */
  export let numOfwinners = 1;
  export let alwaysShowCandidates = 4;
  export let hidePrecincts = false;
  export let face = false;
  export let crosswalk: Record<string, CrosswalkImage> = {};

  let viewAllCandidates = false;

  // reactive so the table updates when the feed polls
  $: tooFewToHide = candidates.length <= alwaysShowCandidates + 1;
  $: hasParties =
    candidates.some((candidate) => candidate.party) &&
    !candidates.every((candidate) => candidate.party?.abbr === 'NP');

  function photoFor(candidate: Choice): CrosswalkImage | undefined {
    if (!face) return undefined;
    return crosswalk[candidate.choiceId] ?? crosswalk.person;
  }
</script>

<div class="contest" data-race-id={raceId} data-winners={numOfwinners}>
  <table class="contest__candidates">
    <CandidateRowHeader {face} {isBallotMeasure} />
    <tbody>
      {#each candidates as candidate, idx (candidate.choiceId)}
        <tr
          class="candidate contest__candidate-row contest__candidate-row-{partyClass(candidate)}"
          hidden={!(viewAllCandidates || tooFewToHide || idx < alwaysShowCandidates)}>
          <CandidateRow
            {candidate}
            lookup={photoFor(candidate)}
            {face}
            {isBallotMeasure}
            {hasParties}
            {totalVotes} />
        </tr>
      {/each}
      {#if !tooFewToHide}
        <tr class="candidate contest__candidate-row">
          <td colspan={face ? 5 : 4} class="candidate__cell view-all">
            <button on:click={() => (viewAllCandidates = !viewAllCandidates)}>
              View
              {viewAllCandidates ? 'fewer' : 'all'}
              candidates
              <svg
                class="icon"
                class:icon-angle-down={!viewAllCandidates}
                class:icon-angle-up={viewAllCandidates}>
                <use
                  href="#{viewAllCandidates ? 'icon-angle-up' : 'icon-angle-down'}" />
              </svg>
            </button>
          </td>
        </tr>
      {/if}
    </tbody>
  </table>
  <div class="table-footer-row">
    <div class="contest__precincts">
      <span class="text-left">
        {#if !hidePrecincts && eevp !== null && (eevp > 0 || totalVotes === 0)}
          {eevp.toFixed(1)}% of expected vote in
        {/if}
      </span>
      <span class="text-right">
        {intcomma(totalVotes)} total vote{pluralize(totalVotes)}
      </span>
    </div>
  </div>
</div>
